import { EmptyState, IconSet, Pagination, Panel } from '@postscript/components';
import {
  isForbiddenError,
  REVIEW_PAGE_SIZE,
  reviewableProposalsParams,
  useOfferProposals,
  useRejectProposal,
} from 'components/brand/offers/hooks/useOfferProposals';
import { ProposalAudienceContext } from 'components/brand/offers/proposalAudience';
import {
  ProposalGroupList,
  ProposalListSkeleton,
} from 'components/brand/offers/ProposalGroupList';
import {
  PROPOSAL_REVIEW_PANEL_STYLE,
  useProposalReviewSections,
} from 'components/brand/offers/ProposalReviewSections';
import {
  OfferProposal,
  ProposalReviewScope,
  PromotionGroup,
} from 'components/brand/offers/types';
import { useEffect, useRef, useState } from 'react';

type Step =
  | { kind: 'list' }
  | { kind: 'detail'; proposal: OfferProposal; sightingCount: number };

const LIST_STEP: Step = { kind: 'list' };

interface DiscoveredOffersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const DiscoveredOffersModal = ({
  isOpen,
  onClose,
}: DiscoveredOffersModalProps): JSX.Element => {
  const [step, setStep] = useState<Step>(LIST_STEP);
  const [page, setPage] = useState(1);
  const headerRef = useRef<HTMLElement>(null);
  const reject = useRejectProposal();
  const proposalsQuery = useOfferProposals(
    {
      ...reviewableProposalsParams(ProposalReviewScope.ALL_PENDING),
      limit: REVIEW_PAGE_SIZE,
      offset: (page - 1) * REVIEW_PAGE_SIZE,
    },
    { enabled: isOpen },
  );
  const forbidden =
    proposalsQuery.isError && isForbiddenError(proposalsQuery.error);
  const totalCount = proposalsQuery.data?.totalCount ?? 0;

  const close = () => {
    setStep(LIST_STEP);
    setPage(1);
    onClose();
  };
  const showList = () => setStep(LIST_STEP);

  const detail = useProposalReviewSections({
    proposal: step.kind === 'detail' ? step.proposal : null,
    sightingCount: step.kind === 'detail' ? step.sightingCount : 0,
    onClose: close,
    onDecided: showList,
    onBack: showList,
    headerRef,
  });

  useEffect(() => {
    if (isOpen && forbidden) close();
  }, [isOpen, forbidden]);

  useEffect(() => {
    if (!proposalsQuery.isSuccess || proposalsQuery.isPreviousData) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / REVIEW_PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [
    page,
    totalCount,
    proposalsQuery.isPreviousData,
    proposalsQuery.isSuccess,
  ]);

  // Panel focuses its content when it opens; a step change inside it does
  // not, so focus moves to the new header for screen readers to announce.
  const stepKey = step.kind === 'detail' ? step.proposal.id : 'list';
  const previousStepKeyRef = useRef(stepKey);
  useEffect(() => {
    if (previousStepKeyRef.current === stepKey) return;
    previousStepKeyRef.current = stepKey;
    if (isOpen) headerRef.current?.focus();
  }, [stepKey, isOpen]);

  const groups: PromotionGroup[] = (
    proposalsQuery.data?.promotions ?? []
  ).filter((group) => group.proposals.length > 0);

  const listContent = (() => {
    if (proposalsQuery.isLoading) return <ProposalListSkeleton />;
    if (proposalsQuery.isError && !proposalsQuery.data) {
      return (
        <EmptyState
          size="small"
          description="The offers could not be loaded. Try again."
          primaryAction={{
            text: 'Retry',
            onClick: () => {
              proposalsQuery.refetch();
            },
          }}
        />
      );
    }
    if (groups.length === 0) {
      return <EmptyState size="small" description="No offers to review." />;
    }
    return (
      <>
        <ProposalGroupList
          aria-label="Offers found"
          groups={groups}
          onReview={(proposal, sightingCount) =>
            setStep({ kind: 'detail', proposal, sightingCount })
          }
          onDismiss={(proposal) => reject.mutate(proposal.id)}
          dismissingId={reject.isLoading ? reject.variables ?? null : null}
        />
        {totalCount > REVIEW_PAGE_SIZE && (
          <Pagination
            currentPage={page}
            selectPage={setPage}
            size="small"
            totalPages={Math.ceil(totalCount / REVIEW_PAGE_SIZE)}
          />
        )}
      </>
    );
  })();

  const sections = detail ?? {
    header: (
      <Panel.Header
        key="list-header"
        ref={headerRef}
        tabIndex={-1}
        icon={IconSet.Tag}
        onCancel={close}
        description="Shopper found these offers on the storefront and in Shopify. AI can make mistakes, so check each offer before creating a draft."
      >
        Offers found on your store
      </Panel.Header>
    ),
    body: <Panel.Body key="list-body">{listContent}</Panel.Body>,
    footer: null,
  };

  return (
    <ProposalAudienceContext.Provider value="merchant">
      <Panel
        isOpen={isOpen && !forbidden}
        onCancel={close}
        placement="modal"
        width="full"
        style={PROPOSAL_REVIEW_PANEL_STYLE}
        data-testid="discovered-offers-modal"
      >
        {sections.header}
        {sections.body}
        {sections.footer}
      </Panel>
    </ProposalAudienceContext.Provider>
  );
};
